import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { ClFarmAlmType } from "@/domains/core"
import { SortDirection, SortType } from "@/domains/table-processor"
import { action, computed, makeObservable, observable, runInAction } from "mobx"

export type ClFarmAlmUiType = ClFarmAlmType | "all"

// Type for sortBy object structure
export interface SortBy {
  key: string
  direction: SortDirection
  type: SortType
}

// Search param sources following the multi-source pattern
export interface SearchParamSources {
  urlParam: string | undefined
  localstorage: string | undefined
  default: string
  ui: string | undefined
}

// SortBy param sources
export interface SortByParamSources {
  urlParam: SortBy | undefined
  localstorage: SortBy | undefined
  default: SortBy
  ui: SortBy | undefined
}

// PageSize param sources
export interface PageSizeParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

// StakedOnly param sources
export interface StakedOnlyParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// ALM param sources (CL Farms specific)
export interface AlmParamSources {
  urlParam: ClFarmAlmUiType | undefined
  localstorage: ClFarmAlmUiType | undefined
  default: ClFarmAlmUiType
  ui: ClFarmAlmUiType | undefined
}

// IsDetailed param sources
export interface IsDetailedParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// CurrentPageNumber param sources
export interface CurrentPageNumberParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

// CurrentPageNumber param sources
export interface DebugSubPagesParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

export class ClFarmsPageStore {
  searchParamSources: SearchParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "",
    ui: undefined,
  }

  sortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "totalValueUsd", direction: "desc", type: "number" },
    ui: undefined,
  }

  pageSizeParamSources: PageSizeParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 10,
    ui: undefined,
  }

  stakedOnlyParamSources: StakedOnlyParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  almParamSources: AlmParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "all",
    ui: undefined,
  }

  isDetailedParamSources: IsDetailedParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  currentPageNumberParamSources: CurrentPageNumberParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 1,
    ui: undefined,
  }

  debugSubPagesParamSources: DebugSubPagesParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
  ) {
    makeObservable(this, {
      almParamSources: observable,
      currentPageNumberParamSources: observable,
      debugSubPagesParamSources: observable,
      isDetailedParamSources: observable,
      pageSizeParamSources: observable,
      searchParamSources: observable,
      sortByParamSources: observable,
      stakedOnlyParamSources: observable,
      alm: computed,
      currentPageNumber: computed,
      isDetailed: computed,
      pageSize: computed,
      search: computed,
      sortBy: computed,
      stakedOnly: computed,
      setAlmFromLocalStorage: action,
      setAlmFromUi: action,
      setAlmFromUrlParam: action,
      setCurrentPageNumberFromLocalStorage: action,
      setCurrentPageNumberFromUi: action,
      setCurrentPageNumberFromUrlParam: action,
      setIsDetailedFromLocalStorage: action,
      setIsDetailedFromUi: action,
      setIsDetailedFromUrlParam: action,
      setPageSizeFromLocalStorage: action,
      setPageSizeFromUi: action,
      setPageSizeFromUrlParam: action,
      setSearchFromLocalStorage: action,
      setSearchFromUi: action,
      setSearchFromUrlParam: action,
      setSortByFromLocalStorage: action,
      setSortByFromUi: action,
      setSortByFromUrlParam: action,
      setStakedOnlyFromLocalStorage: action,
      setStakedOnlyFromUi: action,
      setStakedOnlyFromUrlParam: action,
    })
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  private initializeFromStorage(): void {
    // Initialize search from localStorage
    const localSearch = this.storage.getItem(`${this.chainSlug}-cl-farms-search`)
    if (localSearch) {
      this.setSearchFromLocalStorage(localSearch)
    }

    // Initialize sortBy from localStorage
    const localSortBy = this.storage.getItem(`${this.chainSlug}-cl-farms-sortBy`)
    if (localSortBy) {
      try {
        const parsedSortBy = JSON.parse(localSortBy) as SortBy
        this.setSortByFromLocalStorage(parsedSortBy)
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Initialize pageSize from localStorage
    const localPageSize = this.storage.getItem(`${this.chainSlug}-cl-farms-pageSize`)
    if (localPageSize) {
      const parsedPageSize = Number(localPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromLocalStorage(parsedPageSize)
      }
    }

    // Initialize stakedOnly from localStorage
    const localStakedOnly = this.storage.getItem(`${this.chainSlug}-cl-farms-stakedOnly`)
    if (localStakedOnly) {
      const parsedStakedOnly = localStakedOnly === "true"
      this.setStakedOnlyFromLocalStorage(parsedStakedOnly)
    }

    // Initialize alm from localStorage
    const localAlm = this.storage.getItem(`${this.chainSlug}-cl-farms-alm`)
    if (localAlm && ["all", "algebra", "gamma"].includes(localAlm)) {
      this.setAlmFromLocalStorage(localAlm as ClFarmAlmType)
    }

    // Initialize detailed from localStorage
    const localDetailed = this.storage.getItem(`${this.chainSlug}-cl-farms-is-detailed`)
    if (localDetailed) {
      const parsedDetailed = localDetailed === "true"
      this.setIsDetailedFromLocalStorage(parsedDetailed)
    }

    // Initialize currentPageNumber from localStorage
    const localCurrentPageNumber = this.storage.getItem(`${this.chainSlug}-cl-farms-currentPageNumber`)
    if (localCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(localCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromLocalStorage(parsedCurrentPageNumber)
      }
    }

    // Initialize debugSubPages from localStorage
    const localDebugSubPages = this.storage.getItem(`${this.chainSlug}-cl-farms-debugSubPages`)
    if (localDebugSubPages) {
      const parsedDebugSubPages = localDebugSubPages === "true"
      this.setDebugSubPagesFromLocalStorage(parsedDebugSubPages)
    }
  }

  public initializeFromUrlParams(): void {
    // Initialize search from URL params
    const urlSearch = this.urlParams.getParam("search")
    if (urlSearch) {
      this.setSearchFromUrlParam(urlSearch)
    }

    // Initialize sortBy from URL params (combining sortKey, sortDir, sortType)
    const urlSortKey = this.urlParams.getParam("sortKey")
    const urlSortDir = this.urlParams.getParam("sortDir")
    const urlSortType = this.urlParams.getParam("sortType")

    if (urlSortKey && urlSortDir && urlSortType) {
      const sortBy: SortBy = {
        key: urlSortKey,
        direction: urlSortDir as SortDirection,
        type: urlSortType as SortType,
      }
      this.setSortByFromUrlParam(sortBy)
    }

    // Initialize pageSize from URL params
    const urlPageSize = this.urlParams.getParam("pageSize")
    if (urlPageSize) {
      const parsedPageSize = Number(urlPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromUrlParam(parsedPageSize)
      }
    }

    // Initialize stakedOnly from URL params (URL key is "staked")
    const urlStakedOnly = this.urlParams.getParam("staked")
    if (urlStakedOnly) {
      const parsedStakedOnly = urlStakedOnly === "true"
      this.setStakedOnlyFromUrlParam(parsedStakedOnly)
    }

    // Initialize alm from URL params (URL key is "alm")
    const urlAlm = this.urlParams.getParam("alm")
    if (urlAlm && ["all", "algebra", "gamma"].includes(urlAlm)) {
      this.setAlmFromUrlParam(urlAlm as ClFarmAlmType)
    }

    // Initialize detailed from URL params (URL key is "detailed")
    const urlDetailed = this.urlParams.getParam("detailed")
    if (urlDetailed) {
      const parsedDetailed = urlDetailed === "true"
      this.setIsDetailedFromUrlParam(parsedDetailed)
    }

    // Initialize currentPageNumber from URL params (URL key is "currentPage")
    const urlCurrentPageNumber = this.urlParams.getParam("currentPage")
    if (urlCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(urlCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromUrlParam(parsedCurrentPageNumber)
      }
    }

    // Initialize debugSubPages from URL params (URL key is "debugSubPages")
    const urlDebugSubPages = this.urlParams.getParam("debugSubPages")
    if (urlDebugSubPages) {
      const parsedDebugSubPages = urlDebugSubPages === "true"
      this.setDebugSubPagesFromUrlParam(parsedDebugSubPages)
    }
  }

  get search(): string {
    return (
      this.searchParamSources.ui ??
      this.searchParamSources.urlParam ??
      this.searchParamSources.localstorage ??
      this.searchParamSources.default
    )
  }

  get sortBy(): SortBy {
    return (
      this.sortByParamSources.ui ??
      this.sortByParamSources.urlParam ??
      this.sortByParamSources.localstorage ??
      this.sortByParamSources.default
    )
  }

  get pageSize(): number {
    return (
      this.pageSizeParamSources.ui ??
      this.pageSizeParamSources.urlParam ??
      this.pageSizeParamSources.localstorage ??
      this.pageSizeParamSources.default
    )
  }

  get stakedOnly(): boolean {
    return (
      this.stakedOnlyParamSources.ui ??
      this.stakedOnlyParamSources.urlParam ??
      this.stakedOnlyParamSources.localstorage ??
      this.stakedOnlyParamSources.default
    )
  }

  get alm(): ClFarmAlmUiType {
    return (
      this.almParamSources.ui ??
      this.almParamSources.urlParam ??
      this.almParamSources.localstorage ??
      this.almParamSources.default
    )
  }

  get isDetailed(): boolean {
    return (
      this.isDetailedParamSources.ui ??
      this.isDetailedParamSources.urlParam ??
      this.isDetailedParamSources.localstorage ??
      this.isDetailedParamSources.default
    )
  }

  get currentPageNumber(): number {
    return (
      this.currentPageNumberParamSources.ui ??
      this.currentPageNumberParamSources.urlParam ??
      this.currentPageNumberParamSources.localstorage ??
      this.currentPageNumberParamSources.default
    )
  }

  get debugSubPages(): boolean {
    return (
      this.debugSubPagesParamSources.ui ??
      this.debugSubPagesParamSources.urlParam ??
      this.debugSubPagesParamSources.localstorage ??
      this.debugSubPagesParamSources.default
    )
  }

  setDebugSubPagesFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.debugSubPagesParamSources.urlParam === value) return
    this.debugSubPagesParamSources.urlParam = value
  }

  setDebugSubPagesFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.debugSubPagesParamSources.localstorage === value) return
    this.debugSubPagesParamSources.localstorage = value
  }

  setDebugSubPagesFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.debugSubPagesParamSources.ui === value) return
    this.debugSubPagesParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-cl-farms-debugSubPages`, value.toString())
    this.urlParams.setParam("debugSubPages", value.toString())
  }

  setSearchFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.urlParam === value) return
    this.searchParamSources.urlParam = value
  }

  setSearchFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.localstorage === value) return
    this.searchParamSources.localstorage = value
  }

  setSearchFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.ui === value) return
    this.searchParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-cl-farms-search`, value)
    this.urlParams.setParam("search", value)
  }

  // SortBy setters
  setSortByFromUrlParam(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.urlParam?.key === value.key &&
      this.sortByParamSources.urlParam?.direction === value.direction &&
      this.sortByParamSources.urlParam?.type === value.type
    )
      return
    this.sortByParamSources.urlParam = value
  }

  setSortByFromLocalStorage(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.localstorage?.key === value.key &&
      this.sortByParamSources.localstorage?.direction === value.direction &&
      this.sortByParamSources.localstorage?.type === value.type
    )
      return
    this.sortByParamSources.localstorage = value
  }

  setSortByFromUi(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.ui?.key === value.key &&
      this.sortByParamSources.ui?.direction === value.direction &&
      this.sortByParamSources.ui?.type === value.type
    )
      return
    this.sortByParamSources.ui = value

    // Auto-sync to storage (spread sortBy into individual URL params)
    this.storage.setItem(`${this.chainSlug}-cl-farms-sortBy`, JSON.stringify(value))
    this.urlParams.setParam("sortKey", value.key)
    this.urlParams.setParam("sortDir", value.direction)
    this.urlParams.setParam("sortType", value.type)
  }

  // PageSize setters
  setPageSizeFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.urlParam === value) return
    this.pageSizeParamSources.urlParam = value
  }

  setPageSizeFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.localstorage === value) return
    this.pageSizeParamSources.localstorage = value
  }

  setPageSizeFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.ui === value) return
    this.pageSizeParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-cl-farms-pageSize`, value.toString())
    this.urlParams.setParam("pageSize", value.toString())
  }

  // StakedOnly setters
  setStakedOnlyFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.urlParam === value) return
    this.stakedOnlyParamSources.urlParam = value
  }

  setStakedOnlyFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.localstorage === value) return
    this.stakedOnlyParamSources.localstorage = value
  }

  setStakedOnlyFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.stakedOnlyParamSources.ui === value) return
    this.stakedOnlyParamSources.ui = value

    // Auto-sync to storage (URL key is "staked")
    this.storage.setItem(`${this.chainSlug}-cl-farms-stakedOnly`, value.toString())
    this.urlParams.setParam("staked", value.toString())
  }

  // ALM setters
  setAlmFromUrlParam(value: ClFarmAlmType | undefined): void {
    if (!value) return
    if (this.almParamSources.urlParam === value) return
    this.almParamSources.urlParam = value
  }

  setAlmFromLocalStorage(value: ClFarmAlmType | undefined): void {
    if (!value) return
    if (this.almParamSources.localstorage === value) return
    this.almParamSources.localstorage = value
  }

  setAlmFromUi(value: ClFarmAlmUiType | undefined): void {
    if (!value) return
    if (this.almParamSources.ui === value) return
    this.almParamSources.ui = value

    // Auto-sync to storage (URL key is "alm")
    this.storage.setItem(`${this.chainSlug}-cl-farms-alm`, value)
    this.urlParams.setParam("alm", value)
  }

  // IsDetailed setters
  setIsDetailedFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.urlParam === value) return
    this.isDetailedParamSources.urlParam = value
  }

  setIsDetailedFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.localstorage === value) return
    this.isDetailedParamSources.localstorage = value
  }

  setIsDetailedFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.ui === value) return
    this.isDetailedParamSources.ui = value

    // Auto-sync to storage (URL key is "detailed")
    this.storage.setItem(`${this.chainSlug}-cl-farms-is-detailed`, value.toString())
    this.urlParams.setParam("detailed", value.toString())
  }

  // CurrentPageNumber setters
  setCurrentPageNumberFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.urlParam === value) return
    this.currentPageNumberParamSources.urlParam = value
  }

  setCurrentPageNumberFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.localstorage === value) return
    this.currentPageNumberParamSources.localstorage = value
  }

  setCurrentPageNumberFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.ui === value) return
    this.currentPageNumberParamSources.ui = value

    // Auto-sync to storage (URL key is "currentPage")
    this.storage.setItem(`${this.chainSlug}-cl-farms-currentPageNumber`, value.toString())
    this.urlParams.setParam("currentPage", value.toString())
  }
}
